import * as THREE from 'three';
import type { FeatureEditSession } from '../FeatureEditSession';
import type { RevolveAngleDragTarget } from '../../interaction/revolve';
import { RevolveFeatureEditDefinitionFactory, type RevolveFeatureEditDefinition } from './RevolveFeatureEditDefinition';
import type { RevolveFeatureDefinitionModel } from '../../definition/revolve/RevolveFeatureDefinition';
import type { RevolveFeaturePrototype } from '../../prototype/revolve/RevolveFeaturePrototype';
import {
    BossRevolveSceneResolver,
    type BossRevolveSceneContour,
} from './BossRevolveSceneResolver';
import {
    type BossRevolveAxisResolution,
    type BossRevolveSessionAxisSelection,
    type RevolveModelEdgeSegmentResolver,
} from './RevolveAxisResolver';
import { RevolveAxisProfilePlaneCompatibilityValidator } from './RevolveAxisProfilePlaneCompatibilityValidator';
import { BossRevolveFeatureBindingFactory } from './BossRevolveFeatureBindingFactory';
import { isSketchGroup, type SketchGroup } from '../../../mesh/sketch';

/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/edit/revolve 层；
 * 2. 负责把旋转编辑会话草稿与 prototype 之间做桥接；
 * 3. 该桥接层用于让宿主只持有 UI 草稿，不直接拼装低层旋转命令参数。
 *
 * 文件职责：
 * 1. 从 prototype 恢复旋转编辑定义；
 * 2. 基于会话与编辑定义构建 preview/commit 执行输入；
 * 3. 产出 PropertyManager 所需的轴标签与轮廓标签投影。
 */

/**
 * 旋转 PropertyManager 轮廓项。
 */
export interface BossRevolveSelectedContourItem {
    /** 轮廓 ID。 */
    id: string;
    /** 显示标签。 */
    label: string;
    /** 草图 ID。 */
    sketchId?: string;
    /** 轮廓键。 */
    contourKey?: string;
}

/**
 * 旋转创建态轮廓快照。
 *
 * 设计说明：
 * 1. 闭合区域选择产生的是临时区域 mesh，不能要求后续命令再按场景对象 ID 回查；
 * 2. 快照只保存建模所需的纯数据，禁止把 THREE.Object3D 带入命令/Worker 链路；
 * 3. 草图被修改后由外层刷新快照，SessionBridge 只负责把快照收口为统一的场景轮廓语义。
 */
export interface BossRevolveSessionContourSnapshot {
    /** 轮廓运行时 ID。 */
    id: string;
    /** 轮廓所属草图 ID。 */
    sketchId: string;
    /** 源草图对象 ID；闭合区域可能没有单一源对象。 */
    sourceObjectId?: string;
    /** 持久化匹配用轮廓键。 */
    contourKey?: string;
    /** 轮廓区域几何。 */
    geometry: ProfileRegionGeometry;
    /** 相交闭合区域的持久化身份。 */
    regionIdentity?: PersistedRegionIdentity;
}

/**
 * 旋转编辑会话恢复载荷。
 */
export interface BossRevolveEditSessionPayload {
    /** 编辑会话。 */
    session: FeatureEditSession | null;
    /** 编辑定义。 */
    definition: RevolveFeatureEditDefinition | null;
    /** 轮廓列表。 */
    selectedContours: BossRevolveSelectedContourItem[];
    /** 轴标签。 */
    selectedAxisLabel: string | null;
}

/**
 * 旋转凸台会话选择草稿。
 */
export interface BossRevolveSessionSelection {
    /** 已选轮廓对象 ID 列表。 */
    contourIds: string[];
    /**
     * 已选轮廓的纯数据快照。
     *
     * 闭合区域选择没有可被通用草图轮廓解析器识别的稳定 Object3D，
     * 因此创建态优先使用该快照，ID 列表仅作为旧草图边选择的兜底。
     */
    contours?: BossRevolveSessionContourSnapshot[];
    /** 已选旋转轴。 */
    axis: BossRevolveSessionAxisSelection | null;
}

/**
 * 旋转凸台会话目标。
 */
export interface BossRevolveSessionTarget {
    /** 旋转轮廓。 */
    contours: BossRevolveSceneContour[];
    /** 旋转轴。 */
    axis: BossRevolveAxisResolution;
}

/**
 * 旋转凸台执行输入。
 */
export interface BossRevolveExecutionInput {
    /** 特征种类。 */
    featureKind: RevolveFeatureKind;
    /** 编辑态特征 ID。 */
    featureId?: string | null;
    /** 特征定义。 */
    definition: RevolveFeatureDefinitionModel;
    /** 序列化绑定。 */
    bindings: SerializedFeatureReferenceBinding[];
}

/**
 * 旋转特征会话桥接层。
 */
export class BossRevolveFeatureSessionBridge {
    /** 场景解析器。 */
    private readonly sceneResolver = new BossRevolveSceneResolver();
    /** 旋转轴与轮廓草图平面兼容性校验器。 */
    private readonly axisProfilePlaneValidator = new RevolveAxisProfilePlaneCompatibilityValidator();

    /**
     * 从 prototype 恢复编辑定义。
     * @param prototype 旋转 prototype。
     * @returns 编辑定义。
     */
    public restoreDefinitionFromPrototype(
        prototype: RevolveFeaturePrototype,
    ): RevolveFeatureEditDefinition {
        return RevolveFeatureEditDefinitionFactory.restoreFromDefinition(prototype.getDefinition());
    }

    /**
     * 从 prototype 投影轮廓列表。
     * @param prototype 旋转 prototype。
     * @returns 轮廓显示项。
     */
    public projectSelectedContours(prototype: RevolveFeaturePrototype): BossRevolveSelectedContourItem[] {
        const profileBinding = prototype
            .getBindings()
            .map((binding) => binding.serialize())
            .find((binding) => binding.kind === 'sketch-profile');
        if (profileBinding?.kind !== 'sketch-profile') {
            return [];
        }
        const sketchName = this.resolveSketchDisplayName(profileBinding.sketchId);
        const contourKeys = profileBinding.contourKeys ?? [];
        const fallbackCount = profileBinding.fallbackRegionGeometries?.length ?? 0;
        const itemCount = Math.max(contourKeys.length, fallbackCount);
        return Array.from({length: itemCount}).map((_, index) => ({
            id: contourKeys[index] ?? `fallback-contour-${index}`,
            contourKey: contourKeys[index],
            sketchId: profileBinding.sketchId,
            label: `${sketchName}-轮廓${index + 1}`,
        }));
    }

    /**
     * 从 prototype 投影轴标签。
     * @param prototype 旋转 prototype。
     * @returns 轴标签。
     */
    public projectAxisLabel(prototype: RevolveFeaturePrototype): string | null {
        const axisReference = prototype.getDefinition().getCommandParams().axisReference;
        if (!axisReference) {
            return '旋转轴';
        }
        if (axisReference.kind === 'sketch_edge') {
            return '草图轴';
        }
        if (axisReference.kind === 'model_edge') {
            return '模型边轴';
        }
        return '旋转轴';
    }

    /**
     * 构建旋转预览输入。
     * @param input 输入参数。
     * @returns 预览执行输入。
     * @throws 当新建旋转凸台时缺少创建态选择草稿。
     * @throws 当当前旋转选择草稿无法恢复为特征输入时。
     */
    public buildPreviewInput(input: {
        featureId?: string | null;
        prototype?: RevolveFeaturePrototype;
        featureKind?: RevolveFeatureKind;
        selection?: BossRevolveSessionSelection;
        definition: RevolveFeatureEditDefinition;
        scene?: import('three').Scene | null;
        resolveModelEdgeSegment?: RevolveModelEdgeSegmentResolver;
    }): BossRevolveExecutionInput {
        if (input.prototype) {
            return this.buildInputFromPrototype(input as {
                featureId?: string | null;
                prototype: RevolveFeaturePrototype;
                definition: RevolveFeatureEditDefinition;
            });
        }

        if (!input.selection) {
            throw new Error('旋转凸台缺少创建态选择草稿。');
        }

        const target = this.resolveTargetFromSelection({
            scene: input.scene ?? null,
            selection: input.selection,
            resolveModelEdgeSegment: input.resolveModelEdgeSegment,
        });
        if (!target) {
            throw new Error('当前旋转选择草稿无法恢复为特征输入。');
        }

        const bindings = this.buildSerializedBindings(target, input.scene ?? null);
        const featureKind = input.featureKind ?? 'boss-revolve';
        return {
            featureKind,
            featureId: input.featureId,
            definition: RevolveFeatureEditDefinitionFactory.buildFeatureDefinition(
                this.buildBaseCommandParams(target, bindings, featureKind),
                input.definition,
            ),
            bindings,
        };
    }

    /**
     * 构建旋转提交输入。
     * @param input 输入参数。
     * @returns 提交执行输入。
     */
    public buildCommitInput(input: {
        featureId?: string | null;
        prototype?: RevolveFeaturePrototype;
        featureKind?: RevolveFeatureKind;
        selection?: BossRevolveSessionSelection;
        definition: RevolveFeatureEditDefinition;
        scene?: import('three').Scene | null;
        resolveModelEdgeSegment?: RevolveModelEdgeSegmentResolver;
    }): BossRevolveExecutionInput {
        return this.buildPreviewInput(input);
    }

    /**
     * 构建旋转角度拖拽目标。
     *
     * 设计说明：
     * 1. 拖拽手柄属于 cad-core 的交互能力，宿主只传入选择草稿与角度定义；
     * 2. 创建态使用场景选择恢复原始轴与轮廓，避免 web-client 直接读取草图对象；
     * 3. 编辑态优先从 prototype 与 binding 恢复轮廓，草图隐藏后仍可使用命令参数兜底。
     *
     * @param input 拖拽目标输入。
     * @returns 拖拽目标；条件不足时返回 null。
     */
    public buildDragTarget(input: {
        prototype?: RevolveFeaturePrototype;
        featureKind?: RevolveFeatureKind;
        selection?: BossRevolveSessionSelection;
        definition: RevolveFeatureEditDefinition;
        scene?: import('three').Scene | null;
        resolveModelEdgeSegment?: RevolveModelEdgeSegmentResolver;
    }): RevolveAngleDragTarget | null {
        if (input.prototype) {
            return this.buildDragTargetFromPrototype({
                prototype: input.prototype,
                definition: input.definition,
                scene: input.scene ?? null,
            });
        }

        if (!input.selection) {
            return null;
        }

        const target = this.resolveTargetFromSelection({
            scene: input.scene ?? null,
            selection: input.selection,
            resolveModelEdgeSegment: input.resolveModelEdgeSegment,
        });
        if (!target) {
            return null;
        }
        return this.buildDragTargetFromSessionTarget(target, input.definition);
    }

    /**
     * 构建编辑会话恢复载荷。
     * @param session 编辑会话。
     * @returns 恢复载荷。
     */
    public buildEditSessionPayload(
        session: FeatureEditSession | null,
        expectedFeatureKind: RevolveFeatureKind = 'boss-revolve',
    ): BossRevolveEditSessionPayload {
        if (!session) {
            return {
                session: null,
                definition: null,
                selectedContours: [],
                selectedAxisLabel: null,
            };
        }
        const prototype = session.getPrototype();
        if (!(prototype instanceof Object) || prototype.getFeatureKind() !== expectedFeatureKind) {
            return {
                session,
                definition: null,
                selectedContours: [],
                selectedAxisLabel: null,
            };
        }
        const revolvePrototype = prototype as RevolveFeaturePrototype;
        return {
            session,
            definition: this.restoreDefinitionFromPrototype(revolvePrototype),
            selectedContours: this.projectSelectedContours(revolvePrototype),
            selectedAxisLabel: this.projectAxisLabel(revolvePrototype),
        };
    }

    /**
     * 从创建态选择草稿恢复旋转目标。
     *
     * @param input 解析输入。
     * @returns 旋转会话目标；无法恢复时返回 null。
     */
    public resolveTargetFromSelection(input: {
        scene: import('three').Scene | null;
        selection: BossRevolveSessionSelection;
        resolveModelEdgeSegment?: RevolveModelEdgeSegmentResolver;
    }): BossRevolveSessionTarget | null {
        const selectionContourCount = input.selection.contours?.length ?? input.selection.contourIds.length;
        if (!input.selection.axis || selectionContourCount === 0) {
            return null;
        }

        const contours = this.resolveContoursFromCreateSelection(input.selection, input.scene);
        if (contours.length === 0) {
            return null;
        }

        const sketchId = contours[0]?.sketchId ?? null;
        if (!sketchId || contours.some((contour) => contour.sketchId !== sketchId)) {
            return null;
        }

        const axis = this.sceneResolver.resolveAxisFromSelection({
            scene: input.scene,
            objectId: input.selection.axis.objectId,
            topologyPick: input.selection.axis.topologyPick,
            resolveModelEdgeSegment: input.resolveModelEdgeSegment,
        });
        if (!axis) {
            return null;
        }
        const axisPlaneCompatibility = this.axisProfilePlaneValidator.validate({
            axis,
            contours,
        });
        if (!axisPlaneCompatibility.compatible) {
            return null;
        }

        return {
            contours,
            axis,
        };
    }

    /**
     * 从创建态选择恢复轮廓。
     *
     * @param selection 创建态选择。
     * @param scene 当前场景。
     * @returns 已恢复的旋转轮廓集合。
     */
    private resolveContoursFromCreateSelection(
        selection: BossRevolveSessionSelection,
        scene: import('three').Scene | null,
    ): BossRevolveSceneContour[] {
        if (selection.contours && selection.contours.length > 0) {
            return selection.contours
                .map((contour) => this.buildSceneContourFromSnapshot(contour))
                .filter((contour): contour is BossRevolveSceneContour => contour !== null);
        }
        return selection.contourIds.flatMap((contourId) => {
            const resolved = this.sceneResolver.resolveContoursFromSelection(scene, contourId);
            if (!Array.isArray(resolved)) {
                return [];
            }
            return resolved;
        });
    }

    /**
     * 把纯轮廓快照转换为旋转场景轮廓。
     *
     * @param snapshot 创建态轮廓快照。
     * @returns 旋转场景轮廓；快照不完整时返回 null。
     */
    private buildSceneContourFromSnapshot(
        snapshot: BossRevolveSessionContourSnapshot,
    ): BossRevolveSceneContour | null {
        if (!snapshot.sketchId || snapshot.geometry.outer.length < 3) {
            return null;
        }
        const geometry = this.cloneProfileRegionGeometry(snapshot.geometry);
        return {
            id: snapshot.id,
            sketchId: snapshot.sketchId,
            sourceObjectId: snapshot.sourceObjectId,
            contourKey: snapshot.contourKey ?? snapshot.id,
            geometry,
            basePoint: this.computeProfileCenter(geometry.outer),
            normal: this.normalizeProfileNormal(geometry.normal),
            sceneObject: null,
            regionIdentity: snapshot.regionIdentity
                ? structuredClone(snapshot.regionIdentity)
                : undefined,
        };
    }

    /**
     * 基于旋转目标构建序列化 binding。
     *
     * @param target 旋转目标。
     * @param scene 当前场景。
     * @returns 序列化 binding 集合。
     */
    public buildSerializedBindings(
        target: BossRevolveSessionTarget,
        scene: import('three').Scene | null,
    ): SerializedFeatureReferenceBinding[] {
        const sketchId = target.contours[0]?.sketchId ?? null;
        const sketchGroup = sketchId
            ? (scene?.getObjectByProperty('uuid', sketchId) ?? null)
            : null;

        return BossRevolveFeatureBindingFactory.buildSerializedBindings({
            sketchId,
            sketchGroup: sketchGroup && isSketchGroup(sketchGroup)
                ? sketchGroup as SketchGroup
                : null,
            contours: target.contours.map((contour) => ({
                contourSourceObjectId: contour.sourceObjectId,
                contourKey: contour.contourKey,
                regionGeometry: contour.geometry,
                regionIdentity: contour.regionIdentity,
            })),
            axisReference: target.axis.axisReference,
        });
    }

    /**
     * 从 prototype 构建编辑态输入。
     *
     * @param input 编辑态输入。
     * @returns 执行输入。
     */
    private buildInputFromPrototype(input: {
        featureId?: string | null;
        prototype: RevolveFeaturePrototype;
        definition: RevolveFeatureEditDefinition;
    }): BossRevolveExecutionInput {
        const featureKind = input.prototype.getDefinition().getFeatureKind();
        return {
            featureKind,
            featureId: input.featureId,
            definition: RevolveFeatureEditDefinitionFactory.buildFeatureDefinition(
                input.prototype.getDefinition().getCommandParams(),
                input.definition,
            ),
            bindings: input.prototype.getBindings().map((binding) => binding.serialize()),
        };
    }

    /**
     * 从编辑态 prototype 构建拖拽目标。
     *
     * @param input 编辑态输入。
     * @returns 拖拽目标；无法恢复轮廓时返回 null。
     */
    private buildDragTargetFromPrototype(input: {
        prototype: RevolveFeaturePrototype;
        definition: RevolveFeatureEditDefinition;
        scene: import('three').Scene | null;
    }): RevolveAngleDragTarget | null {
        const commandParams = input.prototype.getDefinition().getCommandParams();
        const sceneContours = this.sceneResolver.resolveContoursFromPrototype(input.scene, input.prototype);
        const contourGeometries = sceneContours.length > 0
            ? sceneContours.map((contour) => contour.geometry)
            : this.resolveRegionGeometriesFromCommandParams(commandParams);
        if (contourGeometries.length === 0) {
            return null;
        }
        return this.buildDragTargetFromGeometry({
            axis: commandParams.axis,
            contours: contourGeometries,
            definition: input.definition,
        });
    }

    /**
     * 从创建态会话目标构建拖拽目标。
     *
     * @param target 创建态会话目标。
     * @param definition 当前编辑定义。
     * @returns 拖拽目标。
     */
    private buildDragTargetFromSessionTarget(
        target: BossRevolveSessionTarget,
        definition: RevolveFeatureEditDefinition,
    ): RevolveAngleDragTarget {
        return this.buildDragTargetFromGeometry({
            axis: target.axis.axis,
            contours: target.contours.map((contour) => contour.geometry),
            definition,
        });
    }

    /**
     * 从纯几何数据构建拖拽目标。
     *
     * @param input 纯几何输入。
     * @returns 拖拽目标。
     */
    private buildDragTargetFromGeometry(input: {
        axis: RevolveParams['axis'];
        contours: ProfileRegionGeometry[];
        definition: RevolveFeatureEditDefinition;
    }): RevolveAngleDragTarget {
        return {
            axis: {
                point: [...input.axis.point] as [number, number, number],
                direction: [...input.axis.direction] as [number, number, number],
            },
            contours: input.contours.map((contour) => ({
                outer: contour.outer.map((point) => [...point] as [number, number, number]),
                holes: contour.holes?.map((hole) => (
                    hole.map((point) => [...point] as [number, number, number])
                )),
            })),
            direction1Angle: input.definition.direction1Angle,
            directionReversed: input.definition.directionReversed,
            direction2Enabled: input.definition.direction2Enabled,
            direction2Angle: input.definition.direction2Angle,
        };
    }

    /**
     * 从命令参数读取轮廓几何兜底。
     *
     * @param commandParams 旋转命令参数。
     * @returns 轮廓几何集合。
     */
    private resolveRegionGeometriesFromCommandParams(commandParams: RevolveParams): ProfileRegionGeometry[] {
        if (Array.isArray(commandParams.regionGeometries) && commandParams.regionGeometries.length > 0) {
            return commandParams.regionGeometries;
        }
        return commandParams.regionGeometry ? [commandParams.regionGeometry] : [];
    }

    /**
     * 克隆轮廓区域几何。
     *
     * @param geometry 原始区域几何。
     * @returns 与输入解耦的区域几何。
     */
    private cloneProfileRegionGeometry(geometry: ProfileRegionGeometry): ProfileRegionGeometry {
        return {
            outer: geometry.outer.map((point) => this.clonePointTuple(point)),
            outerSegments: geometry.outerSegments
                ? structuredClone(geometry.outerSegments)
                : undefined,
            holes: geometry.holes?.map((hole) => hole.map((point) => this.clonePointTuple(point))),
            holeSegments: geometry.holeSegments
                ? structuredClone(geometry.holeSegments)
                : undefined,
            normal: this.clonePointTuple(geometry.normal),
            xAxis: this.clonePointTuple(geometry.xAxis),
            yAxis: this.clonePointTuple(geometry.yAxis),
        };
    }

    /**
     * 克隆三维点元组。
     *
     * @param point 三维点或方向元组。
     * @returns 新的三维元组。
     */
    private clonePointTuple(point: [number, number, number]): [number, number, number] {
        return [point[0], point[1], point[2]];
    }

    /**
     * 计算轮廓点列中心。
     *
     * @param points 轮廓外环点列。
     * @returns 外环点列中心。
     */
    private computeProfileCenter(points: Array<[number, number, number]>): THREE.Vector3 {
        const accumulator = points.reduce(
            (sum, point) => {
                sum.x += point[0];
                sum.y += point[1];
                sum.z += point[2];
                return sum;
            },
            new THREE.Vector3(),
        );
        return accumulator.multiplyScalar(1 / Math.max(points.length, 1));
    }

    /**
     * 归一化轮廓法向。
     *
     * @param normal 轮廓法向。
     * @returns 可用于拖拽与标签投影的单位法向。
     */
    private normalizeProfileNormal(normal: [number, number, number]): THREE.Vector3 {
        const vector = new THREE.Vector3(...normal);
        return vector.lengthSq() > 0 ? vector.normalize() : new THREE.Vector3(0, 0, 1);
    }

    /**
     * 构建旋转命令基础参数。
     *
     * @param target 旋转目标。
     * @param bindings 已序列化 binding。
     * @param featureKind 特征种类。
     * @returns 带默认角度的基础参数，后续会由编辑定义统一覆盖。
     */
    private buildBaseCommandParams(
        target: BossRevolveSessionTarget,
        bindings: SerializedFeatureReferenceBinding[],
        featureKind: RevolveFeatureKind = 'boss-revolve',
    ): RevolveParams {
        const regionGeometries = target.contours.map((contour) => contour.geometry);
        const contourKeys = target.contours.map((contour) => contour.contourKey ?? contour.id);
        return {
            sourceType: 'region_geometry',
            featureKind,
            operation: featureKind === 'cut-revolve' ? 'cut' : 'boss',
            sketchId: target.contours[0]!.sketchId,
            contourKeys,
            regionGeometry: regionGeometries[0],
            regionGeometries,
            regionIdentities: this.resolveRegionIdentities(bindings),
            runtimeRegionIdentities: this.resolveRegionIdentities(bindings),
            axis: {
                point: [...target.axis.axis.point] as [number, number, number],
                direction: [...target.axis.axis.direction] as [number, number, number],
            },
            axisReference: target.axis.axisReference,
            runtimeSketchPlane: this.resolveRuntimeSketchPlane(bindings),
            runtimeSketchEntities: this.resolveRuntimeSketchEntities(bindings),
            direction1Angle: Math.PI * 2,
            direction2Enabled: false,
            direction2Angle: 0,
            mergeResult: true,
            referenceBindings: bindings,
        };
    }

    /**
     * 从 binding 中读取运行时草图平面。
     *
     * @param bindings 序列化 binding 集合。
     * @returns 运行时草图平面。
     */
    private resolveRuntimeSketchPlane(bindings: SerializedFeatureReferenceBinding[]) {
        const profileBinding = bindings.find((binding) => binding.kind === 'sketch-profile');
        return profileBinding?.kind === 'sketch-profile'
            ? profileBinding.runtimeSketchPlane
            : undefined;
    }

    /**
     * 从 binding 中读取运行时草图实体。
     *
     * @param bindings 序列化 binding 集合。
     * @returns 运行时草图实体。
     */
    private resolveRuntimeSketchEntities(bindings: SerializedFeatureReferenceBinding[]) {
        const profileBinding = bindings.find((binding) => binding.kind === 'sketch-profile');
        return profileBinding?.kind === 'sketch-profile'
            ? profileBinding.runtimeSketchEntities
            : undefined;
    }

    /**
     * 从 binding 中读取区域身份。
     * @param bindings 序列化 binding 集合。
     * @returns 区域身份列表。
     */
    private resolveRegionIdentities(bindings: SerializedFeatureReferenceBinding[]) {
        const profileBinding = bindings.find((binding) => binding.kind === 'sketch-profile');
        return profileBinding?.kind === 'sketch-profile'
            ? profileBinding.regionIdentities
            : undefined;
    }

    /**
     * 解析草图显示名称。
     * @param sketchId 草图 ID。
     * @returns 草图显示名称。
     */
    private resolveSketchDisplayName(sketchId: string): string {
        const match = /(\d+)/.exec(sketchId);
        if (!match) {
            return '草图1';
        }
        return `草图${match[1]}`;
    }
}
